/**
 * Client-documents API client (D8 v1 — the record page's Documents tab).
 *
 * Thin client over the 0131 contract (mirrors src/lib/notes.ts fetch
 * semantics: native fetch, ApiError normalization, redirect:'manual' on
 * writes for the X.5.4 auth-expiry signal):
 *
 *   POST   /api/t/{slug}/v1/clients/{clientId}/documents            handshake step 1
 *   (PUT the file bytes to the returned single-use URL)             handshake step 2
 *   POST   /api/t/{slug}/v1/documents/{documentId}/complete         handshake step 3
 *   GET    /api/t/{slug}/v1/clients/{clientId}/documents            list (+ archived_count)
 *   GET    /api/t/{slug}/v1/clients/{clientId}/documents?archived=1 the removed-earlier review list
 *   POST   /api/t/{slug}/v1/documents/{documentId}/download         audited download grant
 *   PATCH  /api/t/{slug}/v1/documents/{documentId}/archive          restore (archived: false)
 *   DELETE /api/t/{slug}/v1/documents/{documentId}                  true deletion (row + stored file; 0141)
 *
 * `uploadDocument` drives all three handshake steps (declare -> PUT ->
 * complete) so components hold ONE mutation. The file's digest is
 * computed client-side (crypto.subtle) and recorded by the complete
 * call. The upload URL is used exactly once and never stored.
 */

import { writeFetch } from './writeFetch';
import { ApiError, apiErrorFrom } from './api';
import { putFileBytes, sha256HexOf } from './blobUpload';

// ---------------------------------------------------------------------
// Wire shapes (per the 0131 contract)
// ---------------------------------------------------------------------

export type DocumentKind =
  | 'assessment_delivered'
  | 'legal_instrument'
  | 'insurance'
  | 'intake'
  | 'medical_record'
  | 'correspondence'
  | 'other';

/** Kind labels (display copy; a document kind is a FACT -> neutral chip). */
export const DOCUMENT_KIND_OPTIONS: readonly { value: DocumentKind; label: string }[] = [
  { value: 'assessment_delivered', label: 'Assessment' },
  { value: 'legal_instrument', label: 'Legal' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'intake', label: 'Intake' },
  { value: 'medical_record', label: 'Medical record' },
  { value: 'correspondence', label: 'Correspondence' },
  { value: 'other', label: 'Other' },
];

export const DOCUMENT_KIND_LABELS: Record<DocumentKind, string> = Object.fromEntries(
  DOCUMENT_KIND_OPTIONS.map((o) => [o.value, o.label]),
) as Record<DocumentKind, string>;

export interface DocumentWire {
  id: string;
  kind: DocumentKind;
  display_name: string;
  content_type: string;
  size_bytes: number;
  created_at: string;
  /** Resolved server-side — never a raw uuid on screen. */
  uploaded_by_display_name: string | null;
  /** Deep links only; never rendered. */
  uploaded_by_user_id: string;
  supersedes_document_id: string | null;
  supersedes_created_at: string | null;
}

export interface DocumentsListResponse {
  rows: DocumentWire[];
  /** Removed-earlier rows for this client (drives the review reveal). */
  archived_count: number;
}

/** One removed-earlier entry (the review list): a row + when it left. */
export interface ArchivedDocumentWire extends DocumentWire {
  archived_at: string | null;
}

export interface ArchivedDocumentsListResponse {
  rows: ArchivedDocumentWire[];
}

export interface DeleteDocumentResponse {
  document_id: string;
  deleted: true;
}

export interface CreateUploadResponse {
  document_id: string;
  upload_url: string;
  upload_expires_at: string;
}

export interface DownloadGrantResponse {
  document_id: string;
  client_id: string;
  download_url: string;
  download_expires_at: string;
  display_name: string;
}

export interface ArchiveResponse {
  document_id: string;
  client_id: string;
  is_active: boolean;
  archived_at: string | null;
}

// ---------------------------------------------------------------------
// Upload-side policy (mirrors validators/clientDocuments.ts)
// ---------------------------------------------------------------------

export const DOCUMENT_MAX_BYTES = 25 * 1024 * 1024;

const ACCEPTED_TYPES: ReadonlySet<string> = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/png',
  'image/jpeg',
  'text/plain',
]);

/** The picker's accept attribute (extensions + mimes). */
export const DOCUMENT_ACCEPT =
  '.pdf,.docx,.png,.jpg,.jpeg,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,text/plain';

/**
 * The PREVIEWABLE subset — the types the browser renders natively and
 * safely in a tab (MIRROR of PREVIEWABLE_CONTENT_TYPES in
 * api/_shared/validators/clientDocuments.ts; keep in lockstep). docx is
 * deliberately absent: no safe way to show it in the browser, so it is
 * Download-only and the server coerces an inline request for it back to
 * a plain download.
 */
export const PREVIEWABLE_CONTENT_TYPES: ReadonlySet<string> = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'text/plain',
]);

/** Whether the Open affordance applies (drives per-row rendering). */
export function isPreviewable(contentType: string): boolean {
  return PREVIEWABLE_CONTENT_TYPES.has(contentType);
}

/** How the grant presents the bytes (mirror of the api-side type). */
export type DocumentDisposition = 'attachment' | 'inline';

const EXTENSION_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  txt: 'text/plain',
};

/**
 * The file's effective content type: the browser-reported type when it
 * is accepted, else an extension fallback (docx in particular reads as
 * empty/oct-stream on some systems). null = not an accepted document.
 */
export function resolveContentType(file: { name: string; type: string }): string | null {
  if (ACCEPTED_TYPES.has(file.type)) return file.type;
  const ext = file.name.toLowerCase().split('.').pop() ?? '';
  return EXTENSION_TYPES[ext] ?? null;
}

/**
 * Lowercase-hex sha256 of the file bytes (recorded at complete).
 * Re-exported so this module's existing importers keep working; the
 * implementation is SHARED with the card-attachments surface
 * (src/lib/blobUpload.ts) so the two can never drift apart.
 */
export { sha256HexOf };

// ---------------------------------------------------------------------
// Fetch plumbing (notes.ts semantics)
// ---------------------------------------------------------------------

function tenantUrl(slug: string, path: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1${path}`;
}

async function parseErrorBody(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return await res.text().catch(() => null);
  }
}

async function get<T>(slug: string, path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(tenantUrl(slug, path), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!res.ok) {
    throw new ApiError(res.status, await parseErrorBody(res), `GET ${path} → HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

async function send<T>(
  method: 'POST' | 'PATCH',
  slug: string,
  path: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  const res = await writeFetch(tenantUrl(slug, path), {
    method,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    redirect: 'manual',
    signal,
  });
  if (!res.ok) {
    throw new ApiError(
      res.status,
      await parseErrorBody(res),
      `${method} ${path} → HTTP ${res.status}`,
    );
  }
  return (await res.json()) as T;
}

/** DELETE with the same redirect/auth-expiry semantics as send(). */
async function del<T>(slug: string, path: string): Promise<T> {
  const res = await writeFetch(tenantUrl(slug, path), {
    method: 'DELETE',
    headers: { Accept: 'application/json' },
    redirect: 'manual',
  });
  if (!res.ok) {
    throw new ApiError(res.status, await parseErrorBody(res), `DELETE ${path} → HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

// Handshake step 2 (the browser PUTs the bytes straight to storage) is
// the shared blobUpload primitive, imported at the top of this module.

// ---------------------------------------------------------------------
// The API surface
// ---------------------------------------------------------------------

export interface UploadDocumentInput {
  kind: DocumentKind;
  file: File;
  /** The name the document is filed and downloaded under; absent, the
   *  file's own name. */
  display_name?: string;
  /** Set = this upload replaces that document (version chain). */
  supersedes_document_id?: string | null;
}

/**
 * A file name parted into the name without its ending and the ending
 * itself. The ending is the last "." followed by 1 to 10 characters with
 * no further dot, and never a dot at position 0: "insurance-card-front.jpg"
 * gives ("insurance-card-front", ".jpg"), "archive.tar.gz" gives
 * ("archive.tar", ".gz"), "notes" and ".env" give themselves and "".
 */
export function splitEnding(fileName: string): { name: string; ending: string } {
  const m = /^(.+)(\.[^.]{1,10})$/.exec(fileName);
  if (m === null) return { name: fileName, ending: '' };
  return { name: m[1]!, ending: m[2]! };
}

/**
 * The name an upload sends: the typed name trimmed; blank sends the
 * fallback (the file's own name); a name that already ends in the ending
 * (any case) goes as typed; otherwise the ending is added. The stored
 * name is the download's filename, so the ending is what lets the
 * machine open it.
 */
export function withEnding(name: string, ending: string, fallback: string): string {
  const typed = name.trim();
  if (typed === '') return fallback;
  if (ending !== '' && typed.toLowerCase().endsWith(ending.toLowerCase())) return typed;
  return `${typed}${ending}`;
}

/**
 * THE cache key for a client's document list (the workItemAttachmentsKey
 * precedent). Shared deliberately: the record page's Documents tab and
 * the intake drawer's client-documents section read the SAME rows, so an
 * upload in either place must refresh the other. Two hand-written key
 * literals would have made that silently untrue.
 */
export function clientDocumentsKey(slug: string, clientId: string): readonly unknown[] {
  return ['kith-documents', slug, clientId];
}

/** The removed-earlier review list's key (record tab only). */
export function clientDocumentsArchivedKey(
  slug: string,
  clientId: string,
): readonly unknown[] {
  return ['kith-documents-archived', slug, clientId];
}

export const documentsApi = {
  list(slug: string, clientId: string, signal?: AbortSignal): Promise<DocumentsListResponse> {
    return get(slug, `/clients/${encodeURIComponent(clientId)}/documents`, signal);
  },

  /**
   * The whole three-step handshake: declare (pending row + single-blob
   * URL) -> PUT the bytes -> complete (verify + flip active). Throws
   * ApiError at whichever step failed; a failed step leaves at most a
   * harmless pending row server-side, so retry = run it again.
   */
  async uploadDocument(
    slug: string,
    clientId: string,
    input: UploadDocumentInput,
  ): Promise<{ document_id: string }> {
    const contentType = resolveContentType(input.file);
    if (contentType === null) {
      throw new ApiError(400, { error: 'invalid_body', field: 'content_type' }, 'unaccepted file type');
    }
    if (input.file.size <= 0 || input.file.size > DOCUMENT_MAX_BYTES) {
      throw new ApiError(400, { error: 'invalid_body', field: 'size_bytes' }, 'file too large');
    }

    const declared = await send<CreateUploadResponse>(
      'POST',
      slug,
      `/clients/${encodeURIComponent(clientId)}/documents`,
      {
        kind: input.kind,
        display_name: input.display_name ?? input.file.name,
        content_type: contentType,
        size_bytes: input.file.size,
        supersedes_document_id: input.supersedes_document_id ?? null,
      },
    );

    await putFileBytes(declared.upload_url, input.file, contentType);

    const sha256_hex = await sha256HexOf(input.file);
    await send('POST', slug, `/documents/${encodeURIComponent(declared.document_id)}/complete`, {
      sha256_hex,
    });

    return { document_id: declared.document_id };
  },

  /**
   * The audited download grant (~5-minute single-file URL).
   * disposition 'inline' = view in the browser (previewable types only —
   * the server coerces anything else back to a plain download).
   */
  downloadGrant(
    slug: string,
    documentId: string,
    disposition: DocumentDisposition = 'attachment',
  ): Promise<DownloadGrantResponse> {
    return send('POST', slug, `/documents/${encodeURIComponent(documentId)}/download`, {
      disposition,
    });
  },

  /**
   * The document's bytes, in the browser: the inline grant, then a GET of
   * the single-file URL it returns. Used by "Read into Insurance", which
   * hands the image to the photo reader without filing anything. Throws
   * ApiError for a refused grant, a refused GET (the storage account's
   * answer), or a GET that never got an answer (status 0).
   */
  async getFileBytes(slug: string, documentId: string): Promise<Blob> {
    const grant = await documentsApi.downloadGrant(slug, documentId, 'inline');
    let res: Response;
    try {
      res = await fetch(grant.download_url);
    } catch {
      throw new ApiError(0, null, `GET document ${documentId} bytes → no answer`);
    }
    if (!res.ok) throw await apiErrorFrom(res, `GET document ${documentId} bytes`);
    return res.blob();
  },

  /** Restore a removed-earlier document (archived: false). */
  setArchived(slug: string, documentId: string, archived: boolean): Promise<ArchiveResponse> {
    return send('PATCH', slug, `/documents/${encodeURIComponent(documentId)}/archive`, {
      archived,
    });
  },

  /** The removed-earlier review list (audited read, fetched on demand). */
  listArchived(
    slug: string,
    clientId: string,
    signal?: AbortSignal,
  ): Promise<ArchivedDocumentsListResponse> {
    return get(slug, `/clients/${encodeURIComponent(clientId)}/documents?archived=1`, signal);
  },

  /** True deletion: the row via the guarded server path, then the file. */
  deleteDocument(slug: string, documentId: string): Promise<DeleteDocumentResponse> {
    return del(slug, `/documents/${encodeURIComponent(documentId)}`);
  },
};
